import { Link } from "react-router-dom";
import {
  Activity,
  ShieldCheck,
  AlertTriangle,
  XCircle,
  Eye,
  FileCheck,
  Clock,
  Download,
  Database,
  Route,
  RotateCw,
  Waves,
  LineChart,
  Loader2,
  Zap,
  HelpCircle,
} from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Progress } from "@/components/ui/progress";
import { SCORE_ITEMS, formatDateTime, formatDuration, fmtNum } from "@/lib/testFormat";

export function CandidateDetailSheet({ candidate, open, onOpenChange, onEvaluate, evaluating }) {
  if (!candidate) return null;
  const isGraded = candidate.result === "PASS" || candidate.result === "FAIL";

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="bg-zinc-950 border-white/10 w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader className="mb-6">
          <SheetTitle className="text-white font-heading">{candidate.name}</SheetTitle>
          <SheetDescription className="text-zinc-400">
            {candidate.id} · {candidate.startMs ? formatDateTime(candidate.startMs) : "No start time"}
          </SheetDescription>
        </SheetHeader>

        <ResultBadge candidate={candidate} />
        <CandidateInfo candidate={candidate} />
        {isGraded && <ScoreBreakdown candidate={candidate} />}
        <SensorSummary candidate={candidate} />
        {candidate.failures?.length > 0 && candidate.result === "FAIL" && (
          <div data-testid="sheet-failures" className="bg-red-500/5 border border-red-500/20 rounded-xl p-4 mb-6">
            <span className="text-xs uppercase tracking-[0.2em] font-mono text-red-400 mb-2 block">Reasons</span>
            <ul className="space-y-1">
              {candidate.failures.map((f) => (
                <li key={f} className="text-sm text-zinc-300">• {f}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex flex-col gap-2">
          <Link
            to={`/test?testId=${encodeURIComponent(candidate.id)}`}
            data-testid="sheet-open-analysis"
            className="flex items-center justify-center gap-2 bg-zinc-800 text-white border border-white/10 font-bold py-3 px-4 rounded-lg hover:bg-zinc-700 transition-colors w-full"
          >
            <LineChart className="w-4 h-4" />
            Open full sensor analysis
          </Link>
          {onEvaluate && candidate.sampleCount > 0 && (
            <button
              type="button"
              data-testid="sheet-evaluate-btn"
              onClick={() => onEvaluate(candidate.id)}
              disabled={!!evaluating}
              className="flex items-center justify-center gap-2 bg-zinc-800 text-white border border-white/10 font-bold py-3 px-4 rounded-lg hover:bg-zinc-700 transition-colors w-full disabled:opacity-50"
            >
              {evaluating === candidate.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
              {candidate.isReference ? "Save reference metrics" : isGraded ? "Re-evaluate" : "Evaluate"}
            </button>
          )}
          {candidate.result === "PASS" && (
            <Link
              to={`/certificate/${candidate.id}`}
              data-testid="sheet-view-certificate"
              className="flex items-center justify-center gap-2 bg-yellow-500 text-black font-bold py-3 px-4 rounded-lg hover:bg-yellow-400 transition-colors w-full"
            >
              <FileCheck className="w-4 h-4" />
              View Certificate
            </Link>
          )}
          {candidate.result === "PASS" && candidate.certificateUrl && (
            <a
              href={candidate.certificateUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="sheet-download-pdf"
              className="flex items-center justify-center gap-2 bg-zinc-800 text-white border border-white/10 font-bold py-3 px-4 rounded-lg hover:bg-zinc-700 transition-colors w-full"
            >
              <Download className="w-4 h-4" />
              Download PDF
            </a>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

const RESULT_STYLES = {
  PASS: { icon: ShieldCheck, color: "text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/20", label: "PASSED" },
  FAIL: { icon: XCircle, color: "text-red-400", bg: "bg-red-500/10 border-red-500/20", label: "FAILED" },
  REFERENCE: { icon: Database, color: "text-sky-400", bg: "bg-sky-500/10 border-sky-500/20", label: "REFERENCE DATA" },
  PENDING: { icon: HelpCircle, color: "text-zinc-300", bg: "bg-zinc-800 border-white/10", label: "NOT EVALUATED" },
  "NO DATA": { icon: AlertTriangle, color: "text-yellow-400", bg: "bg-yellow-500/5 border-yellow-500/20", label: "NO SENSOR DATA" },
};

function ResultBadge({ candidate }) {
  const st = RESULT_STYLES[candidate.result] || RESULT_STYLES.PENDING;
  const Icon = st.icon;
  const sub =
    candidate.result === "REFERENCE"
      ? "Hardware baseline used to build the reference profile"
      : candidate.result === "NO DATA"
        ? "The device saved no sensor samples for this test"
        : candidate.result === "PENDING"
        ? "Run an evaluation to score this test"
        : `Score: ${candidate.overallScore}/100`;
  return (
    <div className="mb-6">
      <div className={`flex items-center gap-3 ${st.bg} border rounded-xl p-4`}>
        <Icon className={`w-8 h-8 ${st.color}`} />
        <div>
          <div data-testid="detail-result" className={`text-xl font-heading font-black ${st.color}`}>{st.label}</div>
          <div className="text-sm text-zinc-400">{sub}</div>
        </div>
      </div>
    </div>
  );
}

function CandidateInfo({ candidate }) {
  const rows = [
    ["Vehicle", candidate.vehicleType],
    ["Licence Type", candidate.licenseType],
    ["Test Centre", candidate.testCenter],
    ["Age", candidate.age != null ? `${candidate.age} years` : null],
    ["RFID", candidate.rfid],
  ];
  return (
    <div data-testid="sheet-candidate-info" className="bg-zinc-900 border border-white/10 rounded-xl p-4 mb-6">
      <span className="text-xs uppercase tracking-[0.2em] font-mono text-zinc-400 mb-3 block">Candidate Info</span>
      <div className="space-y-2">
        {rows.map(([l, v]) => (
          <div key={l} className="flex justify-between text-sm gap-4">
            <span className="text-zinc-500">{l}</span>
            <span className="text-white text-right">{v ?? "—"}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ScoreBreakdown({ candidate }) {
  return (
    <div className="bg-zinc-900 border border-white/10 rounded-xl p-4 mb-6">
      <span className="text-xs uppercase tracking-[0.2em] font-mono text-zinc-400 mb-3 block">Score Breakdown</span>
      <div className="space-y-4">
        {SCORE_ITEMS.map((item) => {
          const Icon = item.icon;
          const score = candidate.scores?.[item.key];
          const has = Number.isFinite(score);
          return (
            <div key={item.key}>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <Icon className="w-3 h-3 text-zinc-500" />
                  <span className="text-xs font-mono text-zinc-400">{item.label} ({item.weight}%)</span>
                </div>
                <span className={`text-sm font-mono font-bold ${score >= 70 ? "text-emerald-400" : "text-red-400"}`}>
                  {has ? score : "—"}
                </span>
              </div>
              <Progress value={has ? score : 0} className="h-1.5 bg-zinc-800" />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SensorSummary({ candidate }) {
  const m = candidate.sensorData || {};
  const seq = (m.detectedSequence || []).map((d) => d[0]).join("-");
  const items = [
    { label: "Duration", value: formatDuration(candidate.durationMs ?? m.durationMs), icon: Clock },
    { label: "Samples", value: candidate.sampleCount ?? "—", icon: Activity },
    { label: "Turns", value: m.turnCount != null ? `${m.turnCount} ${seq ? `(${seq})` : ""}` : "—", icon: Route },
    { label: "Harsh Events", value: m.harshCount ?? "—", icon: AlertTriangle },
    { label: "Jerk RMS", value: m.jerkRms != null ? `${fmtNum(m.jerkRms, 3)} g/s` : "—", icon: Waves },
    { label: "Roll RMS", value: m.rollRms != null ? `${fmtNum(m.rollRms, 1)} °/s` : "—", icon: RotateCw },
  ];
  return (
    <div data-testid="sheet-sensor-summary" className="bg-zinc-900 border border-white/10 rounded-xl p-4 mb-6">
      <span className="text-xs uppercase tracking-[0.2em] font-mono text-zinc-400 mb-3 block">Sensor Summary</span>
      <div className="grid grid-cols-2 gap-3">
        {items.map((item) => {
          const Icon = item.icon;
          return (
            <div key={item.label} className="bg-white/5 rounded-lg p-3">
              <div className="flex items-center gap-1 mb-1">
                <Icon className="w-3 h-3 text-zinc-500" />
                <span className="text-[10px] font-mono text-zinc-500 uppercase tracking-wider">{item.label}</span>
              </div>
              <span className="text-base font-mono font-medium text-white">{item.value}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function CandidateTableRow({ candidate, onView, evaluating }) {
  const graded = candidate.result === "PASS" || candidate.result === "FAIL";
  return (
    <tr
      data-testid={`candidate-row-${candidate.id}`}
      className="border-b border-white/5 hover:bg-white/5 cursor-pointer transition-colors"
      onClick={() => onView(candidate)}
    >
      <td className="p-2 align-middle">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-zinc-800 border border-white/10 flex items-center justify-center text-xs font-mono font-bold text-zinc-300">
            {candidate.photoSeed}
          </div>
          <div>
            <div className="text-sm font-medium text-white">{candidate.name}</div>
            <div className="text-xs text-zinc-500">{candidate.testCenter || "—"}</div>
          </div>
        </div>
      </td>
      <td className="p-2 align-middle font-mono text-xs text-zinc-400">{candidate.id}</td>
      <td className="p-2 align-middle hidden sm:table-cell text-sm text-zinc-400">{candidate.vehicleType || "—"}</td>
      <td className="p-2 align-middle hidden md:table-cell text-xs text-zinc-500 font-mono">
        {candidate.startMs ? formatDateTime(candidate.startMs) : "—"}
      </td>
      <td className="p-2 align-middle">
        {graded ? (
          <div className="flex items-center gap-2">
            <span className={`text-lg font-mono font-bold ${candidate.result === "PASS" ? "text-emerald-400" : "text-red-400"}`}>
              {candidate.overallScore}
            </span>
            <span className="text-xs text-zinc-500">/100</span>
          </div>
        ) : (
          <span className="text-sm text-zinc-600 font-mono">—</span>
        )}
      </td>
      <td className="p-2 align-middle">
        {evaluating ? <Loader2 className="w-4 h-4 animate-spin text-yellow-500" /> : <StatusBadge result={candidate.result} id={candidate.id} />}
      </td>
      <td className="p-2 align-middle text-right">
        <button
          data-testid={`view-detail-${candidate.id}`}
          className="text-zinc-400 hover:text-yellow-500 transition-colors p-1"
          onClick={(e) => {
            e.stopPropagation();
            onView(candidate);
          }}
        >
          <Eye className="w-4 h-4" />
        </button>
      </td>
    </tr>
  );
}

const BADGE = {
  PASS: ["bg-emerald-500/10 text-emerald-400 border-emerald-500/20", "Pass"],
  FAIL: ["bg-red-500/10 text-red-400 border-red-500/20", "Fail"],
  REFERENCE: ["bg-sky-500/10 text-sky-400 border-sky-500/20", "Reference"],
  PENDING: ["bg-zinc-800 text-zinc-400 border-white/10", "Pending"],
  "NO DATA": ["bg-yellow-500/5 text-yellow-500 border-yellow-500/20", "No data"],
};

function StatusBadge({ result, id }) {
  const [cls, label] = BADGE[result] || BADGE.PENDING;
  return (
    <span data-testid={`status-${id}`} className={`${cls} border px-3 py-1 rounded-full text-xs font-mono uppercase tracking-wider`}>
      {label}
    </span>
  );
}
